/**
 * `Skins` — ОФОРМЛЕНИЕ, the skin picker. `docs/design-direction.md` §3, §5.4, §8;
 * `apps/web/src/styles/skins.css`.
 *
 * ## The screen is its own preview
 *
 * A picker for a colour scheme normally has to fake the thing it is choosing —
 * thumbnails, mock phones, a strip of swatches that stands in for a screen. This one
 * does not need to. `lib/skin`'s `chooseSkin` writes `data-skin` on `<html>` on the
 * pointer-down, and because `tokens.css` §3.2 routes every component through a
 * semantic alias, the entire application — including this screen, the mini player and
 * the nav strip underneath it — repaints in that skin in the same frame. The feedback
 * for choosing a skin is the app becoming it.
 *
 * That is also why nothing here is a modal, a preview pane or a confirm step. Tapping
 * a row IS trying it on; tapping another row undoes it.
 *
 * ## What a row is
 *
 * Each row carries a **paint chip**: a plate painted in that skin's own ground, ruled
 * in its own hairline, carrying its name in its own text ink and a bar of its own
 * accent. Those four values come from `SKINS` in `lib/skin`, as literal hexes on an
 * inline `style`, because a chip has to show the skin that is NOT currently applied —
 * `var(--bg)` inside this screen resolves to whichever skin is active and every chip
 * would come out identical.
 *
 * The blurb beneath the chip is set in the *application's* colours rather than the
 * skin's, because it is the app talking about the skin, not the skin talking.
 *
 * ## Selection
 *
 * §5.4's `active` tier: a 3px left rule in `--border-active`, the same mark the
 * now-playing row wears. No tick, no pill, no coloured card — the rule is the whole
 * indicator, and it is the one the rest of the app already uses for "this is the one".
 */

import { useCallback, useState } from 'react';
import type { CSSProperties, JSX } from 'react';

import { StateFrame } from '../app/states/Loading';
import { Halftone } from '../components/primitives/Halftone';
import { cx } from '../components/primitives/Rule';
import type { SkinDefinition, SkinId } from '../lib/skin';
import { DEFAULT_SKIN, SKINS, chooseSkin, readSkin } from '../lib/skin';
import { telegram } from '../lib/telegram';

import styles from './skins.module.css';

/* ══ copy ══════════════════════════════════════════════════════════════════════════ */

/** Sentence case throughout — tracked Cyrillic caps read as shouting (§12.16). */
export const SKINS_TITLE_RU = 'Оформление';

/** The mono meta line. English, because it is quoting print (§11). */
export const SKINS_META = `${String(SKINS.length)} SKINS`;

export const SKINS_SELECTED_RU = 'Выбрано';

/**
 * The one thing a person needs told, and the one thing they cannot see: the choice is
 * not stored on this phone. Everything else on this screen explains itself by being
 * looked at.
 */
export const SKINS_NOTE_RU =
  'Выбор сохраняется за вашим аккаунтом Telegram — на другом телефоне плеер откроется в том же виде.';

/* ══ a row ═════════════════════════════════════════════════════════════════════════ */

interface SkinRowProps {
  readonly skin: SkinDefinition;
  readonly active: boolean;
  readonly onSelect: (id: SkinId) => void;
}

function SkinRow({ skin, active, onSelect }: SkinRowProps): JSX.Element {
  /*
   * Literal hexes, not tokens. This chip must show a skin that is not applied, and
   * `var(--bg)` here would resolve to the ACTIVE skin's ground on every chip.
   */
  const chip: CSSProperties = {
    background: skin.ground,
    borderColor: skin.ink,
    color: skin.text,
  };

  return (
    <li className={styles.row}>
      <button
        type="button"
        className={styles.pick}
        data-active={active ? 'true' : 'false'}
        aria-pressed={active}
        onClick={() => {
          onSelect(skin.id);
        }}
      >
        <span className={styles.chip} style={chip}>
          <span className={styles.chipHead}>
            <span className={cx('t-mono', 't-11', styles.chipStamp)} style={{ color: skin.ink }}>
              {skin.stamp}
            </span>
            {active ? (
              <span className={cx('t-mono', 't-11', styles.chipMark)}>{SKINS_SELECTED_RU}</span>
            ) : null}
          </span>
          <span className={cx('t-display', 't-20', styles.chipName)}>{skin.name}</span>
          {/* Three bars of the skin's own ink: the whole ink budget, at a glance. */}
          <span className={styles.chipInks} aria-hidden="true">
            <i style={{ background: skin.ink }} />
            <i style={{ background: skin.text }} />
            <i style={{ background: skin.ground, borderColor: skin.text }} />
          </span>
        </span>
        <span className={cx('t-ui', 't-15', styles.blurb)}>{skin.blurb}</span>
      </button>
    </li>
  );
}

/* ══ the screen ════════════════════════════════════════════════════════════════════ */

export function Skins(): JSX.Element {
  const [current, setCurrent] = useState<SkinId>(() => readSkin() ?? DEFAULT_SKIN);

  const select = useCallback((id: SkinId): void => {
    /* Applies the attribute, writes the local mirror, and fires the cloud write. The
       repaint is synchronous; the round trip is not awaited by anything. */
    chooseSkin(id);
    setCurrent(id);
    /* Telegram's header, background and bottom bar are painted by the client, not by
       our CSS, so they only follow the skin when we re-assert them (§10.1). Without
       this the bar above the app keeps the previous skin's ground. */
    telegram.claimChrome();
    telegram.haptics.impact('light');
  }, []);

  return (
    <div className={styles.root}>
      <Halftone as="header" className={styles.masthead}>
        <span className={styles.mastheadLines}>
          <h1 className="t-ui t-28" style={{ fontWeight: 700 }}>
            {SKINS_TITLE_RU}
          </h1>
          <p className={cx('t-mono', 't-11')} style={{ color: 'var(--text-3)' }}>
            {SKINS_META}
          </p>
        </span>
      </Halftone>

      <StateFrame grid="plate" state="content" role="group" ariaLabel={SKINS_TITLE_RU}>
        <ul className={styles.list}>
          {SKINS.map((skin) => (
            <SkinRow key={skin.id} skin={skin} active={skin.id === current} onSelect={select} />
          ))}
        </ul>
        <p className={cx('t-ui', 't-13', styles.note)}>{SKINS_NOTE_RU}</p>
      </StateFrame>
    </div>
  );
}
